import { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { TableCell, TableRow, TableBody, Typography } from "@mui/material";
import {
  Select,
  MenuItem,
  Pagination,
  Paper,
  Box,
  Autocomplete,
  TextField,
} from "@mui/material";
import { FiEdit } from "react-icons/fi";
import Controls from "../../../components/controls/Controls";
import useTable from "../../../components/customHelpers/usePaginationTable";
import Loading from "../../../components/customHelpers/Loading";
import Norecords from "../../../components/customHelpers/Norecords";
import Popup from "../../../components/customHelpers/Popup";
import UpdatePurchaseContainer from "../../../components/forms/updatePurchaseContainer";
import PurchaseProduct from "../../../actions/purchaseProduct";
import { pageSizes } from "../../../utils/utils";
import { PurchaseOrder } from "../../../actions/purchaseOrder";
import { Search } from "@mui/icons-material";

const purchaseHeadCells = [
  { id: "name", label: "Name" },
  { id: "quantity", label: "Quantity" },
  { id: "price", label: "Price" },
  { id: "productName", label: "Product Name" },
  { id: "sku", label: "Sku" },
  { id: "purchaseOrder", label: "Purchase Order" },
  { id: "purchaseDate", label: "Purchase Date" },
  { id: "actions", label: "Actions", disableSorting: true },
];

const PurchaseContainers = ({ NotifyMessage }) => {
  const { viewAllPurchasesByContainerWithPages } = PurchaseProduct();
  const { viewAllProducts } = PurchaseProduct();
  const [purchaseContainers, setPurchaseContainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(pageSizes[1]);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputSku, setInputSku] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSku, setSelectedSku] = useState(null);

  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [actualTotal, setActualTotal] = useState(0);
  const [tot, setTot] = useState(1);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [edite, setEdit] = useState(false);
  const [productLists, setProductLists] = useState([]);
  const { viewAllPurchaseOrders } = PurchaseOrder();
  const [records, setRecords] = useState([]);

  const [filterFn, setFilterFn] = useState({
    fn: (items) => items,
  });

  useEffect(() => {
    getPurchasesByContainer();
  }, [page, limit, searchQuery, selectedProduct, selectedSku]);
  useEffect(() => {
    loadPurchaseOrders();
    loadPurchaseProducts();
  }, []);
  const getPurchasesByContainer = () => {
    setLoading(true);
    viewAllPurchasesByContainerWithPages(
      page,
      limit,
      searchQuery,
      null,
      selectedProduct?.id || null,
      selectedSku?.product?.sku
    ).then((data) => {
      if (data.err) {
        NotifyMessage({
          message: data.err,
          type: "error",
        });
      } else if (data.product) {
        const transformedData = data.product.map((item) => ({
          ...item,
          productName: item.purchaseList?.name || "",
          sku: item.purchaseList?.sku || "",
          price: item.purchaseList?.price || "",
          purchaseOrder: item.purchaseList?.purchaseOrder?.name || "",
        }));
        setPurchaseContainers(transformedData);
        setTotalPages(Math.ceil(data.total / limit));
        setActualTotal(data.pagination.total);
        setTot(data.pagination.totalPages);
      }
      setLoading(false);
    });
  };

  const handleSearch = (e) => {
    setPage(1);
    setSearchQuery(e.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    setLimit(event.target.value);
    setPage(1);
  };

  const { TblContainer, TblHead, recordsAfterSortingAndFiltering } = useTable(
    purchaseContainers,
    purchaseHeadCells,
    filterFn
  );

  const startIndex = (page - 1) * limit + 1;
  const endIndex = Math.min(startIndex + limit - 1, actualTotal);
  const handleSearchChange = (event, value) => {
    setPage(1);
    if (value) {
      setSelectedProduct(value);
    } else {
      setSelectedProduct(null);
    }
  };
  const handleSearchSku = (event, value) => {
    setPage(1);
    setSelectedSku(value);
  };
  const loadPurchaseOrders = () => {
    viewAllPurchaseOrders()
      .then((response) => {
        if (response.err) {
          NotifyMessage({
            message: response.err,
            type: "error",
          });
        } else {
          setRecords(response.purchaseOrder);
        }
      })
      .catch((err) => {
        NotifyMessage({
          message: "Failed to fetch purchase orders",
          type: "error",
        });
      })
      .finally(() => {});
  };
  const loadPurchaseProducts = () => {
    viewAllProducts()
      .then((response) => {
        if (response.err) {
          NotifyMessage({
            message: response.err,
            type: "error",
          });
        } else {
          setProductLists(response.products);
        }
      })
      .catch((err) => {
        NotifyMessage({
          message: "Failed to fetch purchase products",
          type: "error",
        });
      })
      .finally(() => {});
  };
  const options = productLists.map((product) => ({
    label: `${product.sku} - ${product.name}`,
    product,
  }));

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );
  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <div
          className="search-header-container"
          style={{ marginBottom: 0, flex: 1, maxWidth: 260, minWidth: 260 }}
        >
          <div className="search-header-flex">
            <div className="search-container">
              <Search className="search_icon" />
              <input
                type="text"
                onChange={handleSearch}
                placeholder="Search Containers"
                className="search-input"
                style={{ width: "100%" }}
              />
            </div>
          </div>
        </div>
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems="flex-end"
          gap={2}
        >
          <Autocomplete
            options={searchTerm ? filteredOptions : []}
            getOptionLabel={(option) => option.label}
            onChange={handleSearchSku}
            onInputChange={(event, newInputValue, reason) => {
              setInputSku(newInputValue);
              if (reason === "clear") {
                setSearchTerm("");
              }
            }}
            value={selectedSku}
            inputValue={inputSku}
            sx={{
              maxWidth: 260,
              minWidth: 260,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="SKU or Product Name"
                variant="outlined"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
          />
          <Autocomplete
            options={records}
            disableClearable={false}
            getOptionLabel={(option) => option.name}
            onChange={handleSearchChange}
            onInputChange={(event, newInputValue, reason) => {
              setInputValue(newInputValue);
              if (reason === "clear") {
                setSelectedProduct(null);
              }
            }}
            value={selectedProduct}
            inputValue={inputValue}
            sx={{
              maxWidth: 260,
              minWidth: 260,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Purchase Order"
                variant="outlined"
              />
            )}
          />
        </Box>
      </Box>
      {loading ? (
        <Loading />
      ) : (
        <Paper style={{ overflowX: "auto" }}>
          <TblContainer>
            <TblHead />
            <TableBody>
              {recordsAfterSortingAndFiltering().length > 0 ? (
                recordsAfterSortingAndFiltering().map((item) => (
                  <TableRow key={item.Id}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.purchaseList?.price}</TableCell>
                    <TableCell>{item.purchaseList?.name}</TableCell>
                    <TableCell>{item.purchaseList?.sku}</TableCell>
                    <TableCell>
                      {item.purchaseList?.purchaseOrder?.name}
                    </TableCell>
                    <TableCell>
                      {new Date(item.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Controls.ActionButton
                        color="primary"
                        onClick={() => {
                          setOpenPopup(true);
                          setRecordForEdit(item);
                          setEdit(true);
                        }}
                      >
                        <FiEdit />
                      </Controls.ActionButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <Norecords col={7} />
              )}
            </TableBody>
          </TblContainer>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              p: 2,
              backgroundColor: "background.paper",
              borderTop: 1,
              borderColor: "divider",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                Rows per page:
              </Typography>
              <Select
                value={limit}
                onChange={handlePageSizeChange}
                size="small"
                sx={{ minWidth: 120 }}
              >
                {pageSizes.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {actualTotal && (
              <Typography variant="body2" sx={{ mr: 2 }}>
                {startIndex}-{endIndex} of {actualTotal}
              </Typography>
            )}

            {purchaseContainers.length > 0 && (
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                hideNextButton={page === tot}
                hidePrevButton={page === 1}
                shape="rounded"
                siblingCount={1}
              />
            )}
          </Box>
        </Paper>
      )}
      <Popup
        title="Purchase Container Form"
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
      >
        <UpdatePurchaseContainer
          openPopup={openPopup}
          NotifyMessage={NotifyMessage}
          setOpenPopup={setOpenPopup}
          recordForEdit={recordForEdit}
          getPurchasesByContainer={getPurchasesByContainer}
        />
      </Popup>
    </>
  );
};

export default PurchaseContainers;
